import React, { useEffect, useMemo, useState } from 'react';
import { UserCheck, Check, X, Info, Shield, BookMarked } from 'lucide-react';
import { User } from '../../types/auth';
import { userService } from '../../services/database/userService';
import { toast } from '../../utils/toast';

/**
 * Teacher Approval Page
 * Duyệt tài khoản giáo viên
 */
const TeacherApproval: React.FC = () => {
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, TeacherInfoDraft>>({});
  const [savingTeacher, setSavingTeacher] = useState<string | null>(null);

  type TeacherInfoDraft = {
    department: string;
    subjects: string;
    experience: string;
    qualifications: string;
  };

  useEffect(() => {
    loadPendingTeachers();
  }, []);

  const loadPendingTeachers = async () => {
    try {
      setLoading(true);
      const result = await userService.getAllUsers({ role: 'teacher', limit: 1000 });
      const teacherList = result.users;
      setTeachers(teacherList);
      const mappedDrafts: Record<string, TeacherInfoDraft> = {};
      teacherList.forEach(teacher => {
        mappedDrafts[teacher.id] = {
          department: teacher.teacherInfo?.department || '',
          subjects: teacher.teacherInfo?.subjects?.join(', ') || '',
          experience: teacher.teacherInfo?.experience ? String(teacher.teacherInfo.experience) : '',
          qualifications: teacher.teacherInfo?.qualifications?.join(', ') || ''
        };
      });
      setDrafts(mappedDrafts);
    } catch (error) {
      console.error('Error loading pending teachers:', error);
      toast.error('Không thể tải danh sách giáo viên chờ duyệt');
    } finally {
      setLoading(false);
    }
  };

  const pendingTeachers = useMemo(
    () => teachers.filter(u => !(u.teacherInfo && u.teacherInfo.isApproved)),
    [teachers]
  );
  const approvedTeachers = useMemo(
    () => teachers.filter(u => u.teacherInfo?.isApproved),
    [teachers]
  );

  const handleDraftChange = (teacherId: string, field: keyof TeacherInfoDraft, value: string) => {
    setDrafts(prev => ({
      ...prev,
      [teacherId]: {
        ...prev[teacherId],
        [field]: value
      }
    }));
  };

  const buildTeacherInfoPayload = (teacher: User, isApproved: boolean) => {
    const draft = drafts[teacher.id] || {
      department: teacher.teacherInfo?.department || '',
      subjects: teacher.teacherInfo?.subjects?.join(', ') || '',
      experience: teacher.teacherInfo?.experience ? String(teacher.teacherInfo.experience) : '',
      qualifications: teacher.teacherInfo?.qualifications?.join(', ') || ''
    };

    const parseList = (value: string) =>
      value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

    return {
      teacherId: teacher.teacherInfo?.teacherId || teacher.id,
      department: draft.department || teacher.teacherInfo?.department || '',
      subjects: parseList(draft.subjects || teacher.teacherInfo?.subjects?.join(', ') || ''),
      experience: draft.experience
        ? Number(draft.experience)
        : teacher.teacherInfo?.experience || 0,
      qualifications: parseList(
        draft.qualifications || teacher.teacherInfo?.qualifications?.join(', ') || ''
      ),
      isApproved,
      approvedAt: isApproved ? new Date().toISOString() : undefined
    };
  };

  const executeUpdate = async (teacher: User, payload: any, successMessage: string) => {
    try {
      setSavingTeacher(teacher.id);
      await userService.updateUser(teacher.id, {
        teacherInfo: payload
      });
      toast.success(successMessage);
      loadPendingTeachers();
    } catch (error) {
      console.error('Error updating teacher info:', error);
      toast.error('Không thể cập nhật giáo viên, vui lòng thử lại');
    } finally {
      setSavingTeacher(null);
    }
  };

  const handleApprove = async (teacher: User) => {
    const payload = buildTeacherInfoPayload(teacher, true);
    await executeUpdate(teacher, payload, `Đã duyệt giáo viên ${teacher.name}`);
  };

  const handleSaveInfo = async (teacher: User) => {
    // ✅ Auto-approve when saving info
    const payload = buildTeacherInfoPayload(teacher, true);
    await executeUpdate(teacher, payload, `Đã lưu thông tin giáo viên ${teacher.name}`);
  };

  const handleReject = async (teacher: User) => {
    if (!confirm(`Bạn có chắc muốn từ chối giáo viên "${teacher.name}"?`)) {
      return;
    }

    try {
      // Downgrade to student role
      await userService.updateUser(teacher.id, {
        role: 'student',
        teacherInfo: undefined,
      });
      toast.success(`Đã từ chối và chuyển về học sinh`);
      loadPendingTeachers();
    } catch (error) {
      console.error('Error rejecting teacher:', error);
      toast.error('Không thể từ chối giáo viên');
    }
  };

  const handleSuspend = async (teacher: User) => {
    const payload = buildTeacherInfoPayload(teacher, false);
    await executeUpdate(teacher, payload, `Đã tạm dừng quyền giáo viên của ${teacher.name}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Duyệt giáo viên</h1>
        <p className="text-gray-600 mt-2">
          {pendingTeachers.length} giáo viên đang chờ duyệt · {approvedTeachers.length} giáo viên đã được duyệt
        </p>
      </div>

      {pendingTeachers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Không có giáo viên chờ duyệt
          </h3>
          <p className="text-gray-600">
            Tất cả tài khoản giáo viên đã được xử lý
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6">
        {teachers.map(teacher => {
          const draft = drafts[teacher.id] || {
            department: '',
            subjects: '',
            experience: '',
            qualifications: ''
          };
          const isApproved = teacher.teacherInfo?.isApproved ?? false;

          return (
            <div key={teacher.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  {teacher.avatar ? (
                    <img src={teacher.avatar} alt={teacher.name} className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                      <span className="text-purple-600 font-bold text-xl">{teacher.name[0]}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-bold text-gray-900">{teacher.name}</h3>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${isApproved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}
                      >
                        {isApproved ? 'Đã duyệt' : 'Chờ duyệt'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{teacher.email}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Đăng ký: {new Date(teacher.createdAt).toLocaleString('vi-VN')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleSaveInfo(teacher)}
                    disabled={savingTeacher === teacher.id}
                    className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                  >
                    <BookMarked className="w-4 h-4" />
                    <span>Lưu thông tin</span>
                  </button>
                  {isApproved ? (
                    <button
                      onClick={() => handleSuspend(teacher)}
                      disabled={savingTeacher === teacher.id}
                      className="flex items-center space-x-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Bỏ duyệt</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleApprove(teacher)}
                      disabled={savingTeacher === teacher.id}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                      <span>Duyệt</span>
                    </button>
                  )}
                  {!isApproved && (
                    <button
                      onClick={() => handleReject(teacher)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <X className="w-4 h-4" />
                      <span>Từ chối</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bộ môn</label>
                  <input
                    value={draft.department}
                    onChange={e => handleDraftChange(teacher.id, 'department', e.target.value)}
                    placeholder="VD: Vật lý"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Môn phụ trách (phân tách bằng dấu phẩy)
                  </label>
                  <input
                    value={draft.subjects}
                    onChange={e => handleDraftChange(teacher.id, 'subjects', e.target.value)}
                    placeholder="Vật lý 10, Lý thuyết dao động..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kinh nghiệm (năm)</label>
                  <input
                    type="number"
                    min={0}
                    value={draft.experience}
                    onChange={e => handleDraftChange(teacher.id, 'experience', e.target.value)}
                    placeholder="VD: 5"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chứng chỉ/Bằng cấp
                  </label>
                  <input
                    value={draft.qualifications}
                    onChange={e => handleDraftChange(teacher.id, 'qualifications', e.target.value)}
                    placeholder="Cử nhân Vật lý, Thạc sĩ Sư phạm..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">Lưu ý</p>
            <p className="text-xs text-blue-700 mt-1">
              Sau khi duyệt, giáo viên sẽ có quyền tạo nội dung và quản lý học sinh. Từ chối sẽ chuyển tài khoản về học sinh.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherApproval;

